/// <reference types="vite-plugin-svgr/client" />
import {Suspense} from 'react';
import * as Sentry from "@sentry/react";
import {BrowserRouter as Router} from "react-router-dom";
import {TourProvider} from "@reactour/tour";

import "./theme.less"
import "./App.scss";

import {ReactComponent as AppError} from "./assets/app_error.svg";
import {ConfigProvider, Result, Typography, theme} from "antd";
import {LoadingIndicator} from "./shared/LoadingIndicator";
import {useDocumentTitle} from "./shared/hooks.tsx";
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {browserTracingIntegration} from "@sentry/react";
import {lazyWithReload} from "./utils/lazyWithReload";

if (import.meta.env.PROD) {
    Sentry.init({
        dsn: "https://5feca9fa470f4d998e1dae4aa3d8e5eb@o1225812.ingest.sentry.io/6371492",
        integrations: [
            browserTracingIntegration({
                traceXHR: true
            }),
        ],
        tracePropagationTargets: ['app.perfectomy.com', 'http://localhost:8000', '/api/'],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 0.05,
        ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'ChunkLoadError',
            'CKEditor',
            // Third-party Bambora script throws inside its own JSON parse.
            'payFieldsScript',
            // Browser-level network noise; the failed request is captured separately.
            'Network Error',
            'AxiosError: Network Error',
            'AxiosError: timeout exceeded',
            'Failed to fetch dynamically imported module',
            'Importing a module script failed',
        ],
        beforeSend(event, hint) {
            // Drop empty unhandled rejections (no message, no value, no stacktrace).
            const exc: any = hint?.originalException;
            if (
                event.exception?.values?.[0]?.value === 'Non-Error promise rejection captured with value: undefined'
                && (exc === undefined || exc === null)
            ) {
                return null;
            }
            return event;
        },
        release: import.meta.env.VITE_APP_VERSION
    });
}

const AppRouterLazy = lazyWithReload(() => import(/*webpackChunkName: "app-router" */'./reactRoutes.tsx'));
const ProvideAuthLazy = lazyWithReload(() => import(/*webpackChunkName: "app-router" */'./auth/provider'));
const IdleLogoutLazy = lazyWithReload(() => import(/*webpackChunkName: "app-router" */'./auth/IdleLogout').then(m => ({default: m.IdleLogout})));
const PasskeyEnrollPromptLazy = lazyWithReload(() => import(/*webpackChunkName: "app-router" */'./auth/PasskeyEnrollPrompt').then(m => ({default: m.PasskeyEnrollPrompt})));
const MfaEnforcementPromptLazy = lazyWithReload(() => import(/*webpackChunkName: "app-router" */'./auth/MfaEnforcementPrompt').then(m => ({default: m.MfaEnforcementPrompt})));
const LoadingIndicatorLazy = lazyWithReload(() => import(/*webpackChunkName: "LoadingIndicator" */"./shared/LoadingIndicator"));
const UpdatesProviderLazy = lazyWithReload(() => import(/*webpackChunkName: "updates-provider" */'./ehr/updates/UpdatesProvider'));


const queryClient = new QueryClient({});


function App() {
    useDocumentTitle('Perfectomy');

    return (
        <ConfigProvider theme={{
            algorithm: theme.defaultAlgorithm,
            // 1) Global tokens
            token: {
                // Colors
                colorPrimary: '#5641FC',      // from @primary-color
                colorBgLayout: '#FFFFFF',     // from @layout-header-background
                colorTextHeading: '#475467',  // from @heading-color

                // For reference, storing your "beige"

                // Typography
                fontSize: 15,                 // from @font-size-base

                // Control Heights (maps to “controlHeight”, “controlHeightLG”, “controlHeightSM”)
                controlHeight: 36,            // from @height-base
                controlHeightLG: 40,          // from @btn-height-lg
                controlHeightSM: 28,          // from @btn-height-sm

                // Global spacing scales
                sizeLG: 26,                   // from @padding-lg
                sizeMD: 18,                   // from @padding-md
                sizeSM: 14,                   // from @padding-sm
                sizeXS: 10,                   // from @padding-xs
                sizeXXS: 6,                   // from @padding-xss

                /**
                 * Z-index values are often not directly controlled by Ant Design tokens,
                 * so you can store them as custom tokens. You can then reference them
                 * in your own styles if needed.
                 */
            },

            // 2) Component-level tokens
            components: {
                Card: {
                    padding: 8,
                    paddingLG: 12,
                },
                Layout: {
                    headerBg: 'white',
                    siderBg: 'white',
                    bodyBg: '#f0f2f5',
                },
                Menu: {
                    itemBg: 'white',               // from @menu-dark-bg
                    itemHoverBg: '#5641FC',          // from @menu-dark-item-hover-bg
                    itemSelectedBg: '#5641FC',       // from @menu-dark-item-active-bg
                    subMenuItemBg: '#2D256C',            // from @menu-dark-inline-submenu-bg
                    itemSelectedColor: 'white',
                    itemHoverColor: 'white',
                    horizontalItemHoverColor: '#5641FC',
                },

                Calendar: {
                    itemActiveBg: '#2D256C',  // from @calendar-item-active-bg
                },

                Segmented: {
                    itemSelectedBg: '#5641FC',
                    itemSelectedColor: 'white',
                    itemActiveBg: '#ECECFD',
                },
                Modal: {
                    headerBg: 'transparent',
                },
                Table: {
                    expandIconBg: '#5641FC',
                    colorIcon: 'white',
                }
                /**
                 * If you need custom button or form item tokens, you can also define them here:
                 * Button: { ... },
                 * Form: { ... },
                 * etc.
                 */
            },
        }}>
            <Suspense fallback={<LoadingIndicator/>}>
                <Sentry.ErrorBoundary fallback={(
                    <Result
                        icon={(
                            <AppError style={{maxWidth: "512px", maxHeight: "512px", margin: "0 auto"}}/>
                        )}
                        title="Unexpected error occurred please reload the page."
                        subTitle="If it still does not work contact support."
                        extra={[
                            <Typography.Link key="home-link" href="https://app.perfectomy.com/">Go
                                Home</Typography.Link>
                        ]}
                    />
                )}>
                    <ProvideAuthLazy>
                        <IdleLogoutLazy/>
                        <PasskeyEnrollPromptLazy/>
                        <MfaEnforcementPromptLazy/>
                        <Router>
                            <TourProvider steps={[]}>
                                <QueryClientProvider client={queryClient}>
                                    <Suspense fallback={<LoadingIndicatorLazy/>}>
                                        <UpdatesProviderLazy>
                                            <AppRouterLazy/>
                                        </UpdatesProviderLazy>
                                    </Suspense>
                                </QueryClientProvider>
                            </TourProvider>
                        </Router>
                    </ProvideAuthLazy>
                </Sentry.ErrorBoundary>
            </Suspense>
        </ConfigProvider>
    );
}

export default App;
